import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import moment from "moment";
import Switch from "@mui/material/Switch"
import GenericSwitchV2 from "Components/Generic/GenericSwitchV2"
import CalendarContent from "./CalendarContent"
///// STYLES
import {
  Overlay,
  BiggestContainer,
  Footer,
  SwitchContainer,
  ValidateBtn,
  DeleteBtn,
  CloseBtn,
} from "Components/CalendarComponent/index.styles"

import { getFormatFromType } from "Components/chartsLibrary/CalendarFilter/calendarUtils.js"

////////////// EXAMPLE OF USE

/*
 * <CalendarComponent
 *    zIndex="5"
 *    positionTop="5rem"
 *    positionLeft="7rem"
 *    mainColor="#008252"
 *    type="months" => "m", "months" and "month" are accepted for months and "d", "days", "day" for days
 *    dateMin="07/10/2019" (nullable)
 *    dateMax="29/05/2020" (nullable)
 *    onDatesValidation={setDate} => function on "Validate" button click, default console.log
 *    disabledMonths={["01/2020", "09/2020"]} (nullable) => only works with years type
 *    disabledDates={["30/07/2012", "27/09/2020", "09/04/2018"]} (nullable)
 *    fullDaysInMonth={true} (nullable) => if type is month and fullDaysInMonth is true and the 2 selected dates are 01/2020
 *                                          and 03/2020, it will return all the days between the two dates
 *                                          insted of just ['01/2020','02/2020','03/2020']
 *    showComparingToggle={true} (nullable) enable the comparing mode (display the switch button)
 *    comparingModeColor="#FFBE3D" primary color of the second calendar
 * />
 */

////////////////////////////

const CalendarComponent = ({
  /** Contenu libre inséré au-dessus du calendrier. Facultatif : sans lui, rien ne change. */
  headerSlot,
  zIndex,
  closeCalendar,
  displayRightSide,
  popUpAnimationRightSide,
  title,
  selectComparisonText,
  validateBtnLabel,
  deleteBtnLabel,
  mainColor,
  position,
  topPosition,
  leftPosition,
  rightPosition,
  type,
  language,
  dateMin,
  dateMax,
  onDatesValidation,
  disabledMonths,
  disabledDates,
  fullDaysInMonth,
  showComparingToggle,
  displayComparingInitial,
  comparingModeColor,
  onlyOneDate,
  returnedDateFormat,
  preselectedFirstDate,
  preselectedSecondDate,
  preselectedThirdDate,
  preselectedFourthDate,
  isMobileView,
  displayMonthFirstLetters,
  partialTrad,
  extraFuncDeleteBtn,
  cleanDates,
  disableBtnIfDatesUnselected,
  // STYLE,
  widthCalendar,
  showBoxShadowContainer,
  showIconCalendar,
  showCloseIcon,
  showDatesSelected,
  showTitle,
  showBorderContainer,
  highLightTodayDate,
}) => {
  const isDaysType = type => ["days", "day", "d"].includes(type)

  const getStepType = type => (isDaysType(type) ? "months" : "years")
  const getStepDisplayedFormat = type => (isDaysType(type) ? "MMMM YYYY" : "YYYY")

  const getMinDateAsTimestamp = (type, dateMin) => {
    if (dateMin === null) return 0
    else if (isDaysType(type)) return moment(dateMin, "DD MM YYYY").format("X")
    return moment(dateMin, "DD MM YYYY").startOf("month").format("X")
  }

  const getMaxDateAsTimestamp = (type, dateMax) => {
    if (dateMax === null) return Infinity
    else if (isDaysType(type)) return moment(dateMax, "DD MM YYYY").format("X")
    return moment(dateMax, "DD MM YYYY").endOf("month").format("X")
  }

  let stepType = getStepType(type)
  let dateMinTS = getMinDateAsTimestamp(type, dateMin)
  let dateMaxTS = getMaxDateAsTimestamp(type, dateMax)

  const [displayComparing, setDisplayComparing] = useState(displayComparingInitial)
  const [removeDates, setRemoveDates] = useState(false)

  const [firstDate, setFirstDate] = useState(preselectedFirstDate ? preselectedFirstDate : null)
  const [secondDate, setSecondDate] = useState(preselectedSecondDate ? preselectedSecondDate : null)
  const [thirdDate, setThirdDate] = useState(preselectedThirdDate ? preselectedThirdDate : null)
  const [fourthDate, setFourthDate] = useState(preselectedFourthDate ? preselectedFourthDate : null)

  const disabledValidationDate = disableBtnIfDatesUnselected && !firstDate

  const resetAllDate = () => {
    setFirstDate(null)
    setSecondDate(null)
    setThirdDate(null)
    setFourthDate(null)
  }

  useEffect(() => {
    onRemoveSelectedDate()
    resetAllDate()
  }, [type, cleanDates])

  useEffect(() => {
    if (preselectedFirstDate) setFirstDate(preselectedFirstDate)
    if (preselectedSecondDate) setSecondDate(preselectedSecondDate)
    if (preselectedThirdDate) setThirdDate(preselectedThirdDate)
    if (preselectedFourthDate) setFourthDate(preselectedFourthDate)
  }, [])

  useEffect(() => {
    var format = getFormatFromType(type)
    const dateFirstTS = firstDate !== null ? moment(firstDate, format).format("X") : 0
    const dateSecondTS = secondDate !== null ? moment(secondDate, format).format("X") : 0

    if (dateFirstTS > dateSecondTS && dateSecondTS !== 0) {
      setFirstDate(secondDate)
      setSecondDate(firstDate)
    }
  }, [firstDate])

  useEffect(() => {
    var format = getFormatFromType(type)
    const dateFirstTS = firstDate !== null ? moment(firstDate, format).format("X") : 0
    const dateSecondTS = secondDate !== null ? moment(secondDate, format).format("X") : 0

    if (dateSecondTS < dateFirstTS && dateSecondTS !== 0) {
      setSecondDate(firstDate)
      setFirstDate(secondDate)
    }
  }, [secondDate])

  const isNotDisableDay = dayDate => {
    return !disabledDates.includes(dayDate)
  }

  const isNotDisableMonth = monthDate => {
    return !disabledMonths.includes(monthDate)
  }

  const getFormattedDate = (momentDate, stepType) => {
    // return (stepType === "months") ? momentDate.format("DD/MM/YYYY") : momentDate.format("MM/YYYY")
    return momentDate.format(returnedDateFormat) // Not sure about this, need to talk with others
  }

  const getDateArray = (stepType, startDate, endDate, fullDaysInMonth) => {
    var dateArrayToReturn = []

    let dateToAdd = moment(startDate, "DD MM YYYY")
    let dateToAddFormatted = getFormattedDate(dateToAdd, stepType)

    if (endDate === null) return [dateToAddFormatted]

    let lastDate = moment(endDate, "DD MM YYYY")

    while (dateToAdd.isSameOrBefore(lastDate)) {
      dateToAddFormatted = getFormattedDate(dateToAdd, stepType)
      if (stepType === "months" || fullDaysInMonth) {
        // add all days between the first and the last dates
        if (isNotDisableDay(dateToAddFormatted)) dateArrayToReturn.push(dateToAddFormatted)
        dateToAdd.add(1, "days")
      } else {
        // add all months between the first and the last dates
        if (isNotDisableMonth(dateToAddFormatted)) dateArrayToReturn.push(dateToAddFormatted)
        dateToAdd.add(1, "months")
      }
    }

    return dateArrayToReturn
  }

  const onRemoveSelectedDate = () => {
    setFirstDate(null)
    setSecondDate(null)
    setRemoveDates(true)
  }

  const onRemoveComparedSelectedDate = () => {
    setThirdDate(null)
    setFourthDate(null)
    setRemoveDates(true)
  }

  const onValidateBtnClick = () => {
    if (firstDate === null && thirdDate !== null) {
      alert(partialTrad.YOU_DID_NOT_SELECT_ANALYSIS_PERIOD)
      return
    }

    const isCompared = displayComparing && thirdDate !== null

    var dateArrayToReturn = []

    if (isCompared) {
      dateArrayToReturn = [
        [firstDate, secondDate],
        [thirdDate, fourthDate],
      ]
    } else {
      dateArrayToReturn = [firstDate, secondDate]
    }
    if (firstDate === null && secondDate === null) dateArrayToReturn = []
    onDatesValidation(dateArrayToReturn, type, isCompared)
    closeCalendar()
  }

  const setFirstAndSecondDate = date => {
    setFirstDate(date)
    setSecondDate(date)
  }

  const setThirdAndForthDate = date => {
    setThirdDate(date)
    setFourthDate(date)
  }

  const clickDeleteBtn = extraFuncDeleteBtn => {
    extraFuncDeleteBtn()
    onRemoveSelectedDate(true)
  }

  return (
    <>
      {/* <Overlay id="overlay" onClick={closeCalendar} zIndex={toNumber(zIndex) - 1}/> */}
      <BiggestContainer
        id="BiggestContainer"
        role="CalendarComponentMainContainer"
        className={displayRightSide || popUpAnimationRightSide ? "scale-up-tr" : "scale-up-tl"}
        zIndex={zIndex}
        position={position}
        topPosition={topPosition}
        leftPosition={leftPosition}
        rightPosition={rightPosition}
        style={displayRightSide ? { right: "0" } : {}}
        width={widthCalendar}
        showBoxShadowContainer={showBoxShadowContainer}
        showBorderContainer={showBorderContainer}
        mainColor={mainColor}
      >
        {/* Emplacement libre AU-DESSUS du calendrier — la saisie directe des dates s'y insère.
            Non fourni, rien ne s'affiche : aucun des écrans existants ne change. */}
        {headerSlot}
        <CalendarContent
          title={title}
          stepType={stepType}
          type={type}
          language={language}
          mainColor={mainColor}
          dateMinTS={dateMinTS}
          dateMaxTS={dateMaxTS}
          disabledDates={disabledDates}
          disabledMonths={disabledMonths}
          fullDaysInMonth={fullDaysInMonth}
          firstDate={firstDate}
          preselectedFirstDate={preselectedFirstDate}
          setFirstDate={onlyOneDate ? setFirstAndSecondDate : setFirstDate}
          secondDate={secondDate}
          preselectedSecondDate={preselectedSecondDate}
          setSecondDate={onlyOneDate ? setFirstAndSecondDate : setSecondDate}
          removeDates={removeDates}
          setRemoveDates={setRemoveDates}
          format={getStepDisplayedFormat(type)}
          closeCalendar={closeCalendar}
          isMobileView={isMobileView}
          displayMonthFirstLetters={displayMonthFirstLetters}
          partialTrad={partialTrad}
          widthCalendar={widthCalendar}
          showIconCalendar={showIconCalendar}
          showCloseIcon={showCloseIcon}
          showDatesSelected={showDatesSelected}
          showTitle={showTitle}
          highLightTodayDate={highLightTodayDate}
        />
        <Footer
          id="calendarFooter"
          style={
            showComparingToggle
              ? { justifyContent: "space-between", padding: "1rem 0.5rem 0.5rem 0.5rem" }
              : {}
          }
        >
          {showComparingToggle && (
            <SwitchContainer id="displayComparingSwitchContainer" switchColor={comparingModeColor}>
              <GenericSwitchV2
                sliderColorOn={comparingModeColor}
                onToggleChange={() => setDisplayComparing(!displayComparing)}
                value={displayComparing}
                role={"displayComparingSwitch"}
              />
              <div style={{ marginLeft: "5px" }}>{selectComparisonText}</div>
            </SwitchContainer>
          )}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <DeleteBtn
              role="removeDateButton"
              color={mainColor}
              onClick={() => clickDeleteBtn(extraFuncDeleteBtn)}
            >
              {deleteBtnLabel}
            </DeleteBtn>
            {!displayComparing && (
              <ValidateBtn
                role="validateDateButton"
                color={mainColor}
                onClick={disabledValidationDate ? () => {} : onValidateBtnClick}
                disabled={disabledValidationDate}
              >
                {validateBtnLabel}
              </ValidateBtn>
            )}
          </div>
        </Footer>
        {displayComparing && (
          <>
            <CalendarContent
              stepType={stepType}
              type={type}
              language={language}
              mainColor={comparingModeColor}
              dateMinTS={dateMinTS}
              dateMaxTS={dateMaxTS}
              disabledDates={disabledDates}
              disabledMonths={disabledMonths}
              fullDaysInMonth={fullDaysInMonth}
              firstDate={thirdDate}
              preselectedFirstDate={preselectedThirdDate}
              setFirstDate={onlyOneDate ? setThirdAndForthDate : setThirdDate}
              secondDate={fourthDate}
              preselectedSecondDate={preselectedFourthDate}
              setSecondDate={onlyOneDate ? setThirdAndForthDate : setFourthDate}
              removeDates={removeDates}
              setRemoveDates={setRemoveDates}
              format={getStepDisplayedFormat(type)}
              compared={true}
              isMobileView={isMobileView}
              displayMonthFirstLetters={displayMonthFirstLetters}
              partialTrad={partialTrad}
              showIconCalendar={showIconCalendar}
              showCloseIcon={false}
              showDatesSelected={showDatesSelected}
              showTitle={showTitle}
              highLightTodayDate={highLightTodayDate}
            />
            <Footer
              id="comparedCalendarFooter"
              style={{
                backgroundColor: "#F4F4F4",
                margin: "-0.5rem",
                padding: "1rem 0.5rem 0.5rem 0.5rem",
                borderRadius: "0 0 5px 5px",
              }}
            >
              <div style={{ display: "flex", flexDirection: "row" }}>
                <DeleteBtn color={mainColor} onClick={() => onRemoveComparedSelectedDate()}>
                  {deleteBtnLabel}
                </DeleteBtn>
                <ValidateBtn color={mainColor} onClick={onValidateBtnClick}>
                  {validateBtnLabel}
                </ValidateBtn>
              </div>
            </Footer>
          </>
        )}
      </BiggestContainer>
    </>
  )
}

CalendarComponent.propTypes = {
  headerSlot: PropTypes.node,
  position: PropTypes.string,
  topPosition: PropTypes.string,
  leftPosition: PropTypes.string,
  rightPosition: PropTypes.string,
  zIndex: PropTypes.string,
  closeCalendar: PropTypes.func,
  displayRightSide: PropTypes.bool,
  popUpAnimationRightSide: PropTypes.bool,
  title: PropTypes.string,
  validateBtnLabel: PropTypes.string,
  deleteBtnLabel: PropTypes.string,
  mainColor: PropTypes.string,
  type: PropTypes.oneOf(["year", "month", "day", "trimester", "semester"]),
  language: PropTypes.string,
  dateMin: PropTypes.string,
  dateMax: PropTypes.string,
  onDatesValidation: PropTypes.func,
  disabledDates: PropTypes.array,
  disabledMonths: PropTypes.array,
  fullDaysInMonth: PropTypes.bool,
  showComparingToggle: PropTypes.bool,
  comparingModeColor: PropTypes.string,
  displayComparingInitial: PropTypes.bool,
  onlyOneDate: PropTypes.bool,
  returnedDateFormat: PropTypes.string,
  preselectedFirstDate: PropTypes.string,
  preselectedSecondDate: PropTypes.string,
  preselectedThirdDate: PropTypes.string,
  preselectedFourthDate: PropTypes.string,
  isMobileView: PropTypes.bool,
  widthCalendar: PropTypes.string,
  showBoxShadowContainer: PropTypes.bool,
  showIconCalendar: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  extraFuncDeleteBtn: PropTypes.func,
  showDatesSelected: PropTypes.bool,
  showTitle: PropTypes.bool,
  showBorderContainer: PropTypes.bool,
  cleanDates: PropTypes.bool,
  disableBtnIfDatesUnselected: PropTypes.bool,
  highLightTodayDate: PropTypes.bool,
}

CalendarComponent.defaultProps = {
  zIndex: "5",
  closeCalendar: () => {},
  displayRightSide: false,
  popUpAnimationRightSide: false,
  title: "Sélection d'une période d'analyse",
  selectComparisonText: "Sélectionner une période de comparaison",
  validateBtnLabel: "Valider", // Trad?.CONFIRM
  deleteBtnLabel: "Effacer", // Trad?.EFFACER
  mainColor: "#008252",
  position: "absolute",
  topPosition: "",
  leftPosition: "",
  rightPosition: "",
  type: "day",
  language: "fr",
  dateMin: null,
  dateMax: null,
  onDatesValidation: () => {},
  disabledDates: [],
  disabledMonths: [],
  fullDaysInMonth: false,
  showComparingToggle: true,
  displayComparingInitial: false,
  comparingModeColor: "#FFBE3D",
  onlyOneDate: false,
  returnedDateFormat: "MM/YYYY",
  preselectedFirstDate: "",
  preselectedSecondDate: "",
  preselectedThirdDate: "",
  preselectedFourthDate: "",
  isMobileView: false,
  widthCalendar: "36rem",
  showBoxShadowContainer: true,
  showIconCalendar: true,
  showCloseIcon: true,
  extraFuncDeleteBtn: () => {},
  showDatesSelected: false,
  showTitle: true,
  showBorderContainer: false,
  cleanDates: false,
  disableBtnIfDatesUnselected: false,
  highLightTodayDate: true,
}

export default CalendarComponent
